import React, { useState } from "react";
import { useCreateProduct } from "../../../../api/products/products";
import { notifyError } from "../../../../utils/ToastifyProvider";

// 🔁 Reusable Input Field Component
const InputField = ({
  label,
  field,
  value,
  onChange,
  type = "text",
  required = false,
  min,
}) => (
  <div>
    <label className="block text-sm font-semibold mb-1 text-gray-700">
      {required && <span className="text-red-500">*</span>} {label}
    </label>
    <input
      type={type}
      placeholder={`Enter ${label}`}
      value={value}
      onChange={(e) => onChange(field, e.target.value)}
      required={required}
      min={min}
      className="w-full border border-gray-300 rounded-md py-2 px-4 focus:outline-none focus:ring-2 focus:ring-primary"
    />
  </div>
);

// 🔁 Add Product Form Component
const AddProduct = () => {
  const [formData, setFormData] = useState({
    sku: "",
    hsn: "",
    name: "",
    price: "",
    gst: "",
    weight: "",
    length: "",
    breadth: "",
    height: "",
  });

  const { mutate, isLoading } = useCreateProduct();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      sku: "",
      hsn: "",
      name: "",
      price: "",
      gst: "",
      weight: "",
      length: "",
      breadth: "",
      height: "",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const allFieldsFilled = Object.values(formData).every(
      (val) => val !== "" && val !== null
    );
    if (!allFieldsFilled) {
      notifyError("Please fill out all required fields.");
      return;
    }

    const numericFields = [
      "price",
      "gst",
      "weight",
      "length",
      "breadth",
      "height",
    ];

    for (let field of numericFields) {
      const value = parseFloat(formData[field]);
      if (isNaN(value) || value <= 0) {
        notifyError(`${field.toUpperCase()} must be a positive number.`);
        return;
      }
    }

    mutate(formData, {
      onSuccess: () => resetForm(),
      onError: (err) => {
        notifyError(err.response?.data?.message || "Something went wrong.");
        console.error("Add Product Error:", err);
      },
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-10">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl bg-gray-50 border border-gray-300 rounded-lg shadow p-8"
      >
        <h2 className="text-3xl font-bold text-center mb-6 text-black">
          Add Product
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="SKU"
            field="sku"
            value={formData.sku}
            onChange={handleChange}
            required
          />
          <InputField
            label="HSN"
            field="hsn"
            value={formData.hsn}
            onChange={handleChange}
            required
          />
          <InputField
            label="Name"
            field="name"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <InputField
            label="Price"
            field="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            required
            min={1}
          />

          <InputField
            label="GST (%)"
            field="gst"
            type="number"
            value={formData.gst}
            onChange={handleChange}
            required
            min={1}
          />
          <InputField
            label="Weight (kg)"
            field="weight"
            type="number"
            value={formData.weight}
            onChange={handleChange}
            required
          
          />
          <InputField
            label="Length (cm)"
            field="length"
            type="number"
            value={formData.length}
            onChange={handleChange}
            required
            min={0}
          />
          <InputField
            label="Breadth (cm)"
            field="breadth"
            type="number"
            value={formData.breadth}
            onChange={handleChange}
            required
            min={0}
          />
          <InputField
            label="Height (cm)"
            field="height"
            type="number"
            value={formData.height}
            onChange={handleChange}
            required
             min={0}
          />
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button
            type="submit"
            disabled={isLoading}
            className={`bg-primary text-white rounded-full px-6 py-2 font-medium hover:opacity-90 transition ${
              isLoading ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;
